import React, { useEffect, useMemo, useState } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";

/**
 * Shared premium layout for Medily legal pages.
 * Props:
 *   title      – <h1> heading for the page
 *   subtitle   – short lede sentence beneath the title
 *   updated    – "Last updated" ISO/text string
 *   sections   – array of { id, heading, body: ReactNode | string[] }
 *                Auto-builds a sticky table of contents from the section headings.
 *   status     – optional pre-content status message
 *                (e.g. "This document is being finalised — the final wording will be published shortly.")
 */
const LegalPage = ({ title, subtitle, updated, sections = [], status }) => {
  const [activeId, setActiveId] = useState(sections[0]?.id || "");

  useEffect(() => {
    if (!sections.length) return;
    const ob = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveId(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) ob.observe(el);
    });
    return () => ob.disconnect();
  }, [sections]);

  const hasToc = useMemo(() => sections.length >= 3, [sections.length]);

  return (
    <div className="relative min-h-screen bg-white">
      {/* Soft blue ambient glows for Medily's signature aesthetic */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[560px] -z-10 overflow-hidden bg-[radial-gradient(1200px_500px_at_50%_-10%,#eaf0fb_0%,transparent_70%)]"
      />
      <Navbar />

      <header className="relative pt-16 md:pt-24 pb-10 md:pb-14">
        <div className="max-w-[840px] mx-auto px-6 md:px-8">
          <p className="text-[11.5px] font-bold tracking-[0.22em] uppercase text-[#1d4ed8]">
            Legal
          </p>
          <h1
            className="mt-3 text-[36px] md:text-[52px] font-semibold text-slate-900 tracking-tight leading-[1.05]"
            data-testid="legal-page-title"
          >
            {title}
          </h1>
          {subtitle && (
            <p className="mt-4 text-[16.5px] leading-relaxed text-slate-500 max-w-[640px]">
              {subtitle}
            </p>
          )}
          {updated && (
            <p className="mt-6 text-[13px] text-slate-400" data-testid="legal-page-updated">
              Last updated: <span className="text-slate-600">{updated}</span>
            </p>
          )}
        </div>
      </header>

      <main className="pb-24">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          {status && (
            <div
              className="mb-10 rounded-2xl border border-[#dbe6ff] bg-[#f4f7fd] p-5 md:p-6 text-[14.5px] leading-relaxed text-slate-700"
              data-testid="legal-page-status"
            >
              {status}
            </div>
          )}

          <div className={hasToc ? "grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-12" : ""}>
            {hasToc && (
              <aside className="hidden lg:block">
                <nav
                  aria-label="On this page"
                  className="sticky top-28"
                  data-testid="legal-toc"
                >
                  <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-slate-400">
                    On this page
                  </p>
                  <ul className="mt-3 space-y-1.5">
                    {sections.map((s) => (
                      <li key={s.id}>
                        <a
                          href={`#${s.id}`}
                          className={`block text-[13.5px] leading-snug py-1.5 pl-3 border-l-2 transition ${
                            activeId === s.id
                              ? "border-[#1d4ed8] text-[#1d4ed8] font-medium"
                              : "border-transparent text-slate-500 hover:text-slate-800"
                          }`}
                          data-testid={`legal-toc-link-${s.id}`}
                        >
                          {s.heading}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </aside>
            )}

            <article className="min-w-0 max-w-[720px]">
              {sections.map((s) => (
                <section key={s.id} id={s.id} className="scroll-mt-28 mb-10 md:mb-12">
                  <h2 className="text-[22px] md:text-[26px] font-semibold text-slate-900 tracking-tight">
                    {s.heading}
                  </h2>
                  <div className="mt-4 space-y-4 text-[15.5px] leading-[1.75] text-slate-700">
                    {Array.isArray(s.body)
                      ? s.body.map((p, i) =>
                          typeof p === "string" ? <p key={i}>{p}</p> : <React.Fragment key={i}>{p}</React.Fragment>
                        )
                      : s.body}
                  </div>
                </section>
              ))}
            </article>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LegalPage;
